import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useInView } from 'react-intersection-observer';
import { faCommentDots } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { Item, Select } from '@components/basics/forms/inputs/Select';
import { Button } from '@components/main-components/button/Button';
import { Modal } from '@components/main-components/modals/Modal';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { useLogError } from '@hooks/useLogErrorNew';
import { useMultipleNftsForRelease } from '@hooks/useMultipleNftsForRelease';
import { DataTestId } from '@utils/test';
import { SetCommentModal } from './SetComment';
import { SetTimeSlotModal } from './SetTimeSlot';
import { ShareToSocialModal } from './ShareToSocial';
import { StatusBar } from './StatusBar';
import {
  ButtonWrapper,
  containerStyles,
  Content,
  headerStyles,
  InstructionHelper,
  ModalWrapper,
} from './styles';

export function SelectEditionModal({ releaseId }: { releaseId: string }) {
  const logError = useLogError();
  const { tablet } = MediaQuery.useContainer();
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.1,
  });
  const { nftsOwnedByUser, loadMore, loading } = useMultipleNftsForRelease({ releaseId });

  useEffect(() => {
    if (isAtBottom) {
      loadMore();
    }
  }, [isAtBottom, loadMore]);

  const nfts = nftsOwnedByUser;
  const [index, setIndex] = useState<number>(0);
  const selectedNft = nfts?.[index];

  const commentedChip = (
    <Chip>
      <FontAwesomeIcon icon={faCommentDots} size="lg" />
      <p>Commented</p>
    </Chip>
  );

  const onNextClick = () => {
    if (selectedNft?.songSlot != null) {
      openModal(ModalType.SET_COMMENT, {
        body: <SetCommentModal releaseId={releaseId} nftId={selectedNft.id} />,
      });
    } else if (selectedNft) {
      openModal(ModalType.SET_TIMESLOT, {
        body: (
          <SetTimeSlotModal
            releaseId={releaseId}
            type="selected"
            selectedNftId={selectedNft.id}
            selectedNftSongSlot={selectedNft.songSlot}
          />
        ),
      });
    }
  };

  const openShareModal = useStableCallback(() => {
    if (!selectedNft) {
      const message = 'Error skipping commenting from select edition, no nft';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_NFT_ID,
        message,
        toast: 'Please refresh and try again',
        level: 'fatal',
        isArtistOnlyPage: false,
        pillar: PILLARS.MINTING,
        indexedTags: {
          releaseId,
        },
      });
      return;
    }

    openModal(ModalType.SHARE_TO_SOCIAL, {
      body: <ShareToSocialModal releaseId={releaseId} nftId={selectedNft.id} />,
    });
  });

  const footer = (
    <ButtonWrapper>
      <Button
        fullWidth={!tablet}
        className="mr-2"
        data-testid={DataTestId.SET_TIMESLOT_MODAL_SKIP_BUTTON}
        label="Skip"
        variant="secondary"
        disabled={!selectedNft}
        onClick={openShareModal}
      />
      <Button
        fullWidth={!tablet}
        label="Next"
        variant="primary"
        onClick={onNextClick}
        disabled={!selectedNft}
        loading={loading}
      />
    </ButtonWrapper>
  );

  const body = (
    <ModalWrapper>
      <StatusBar releaseId={releaseId} />

      <Content>
        <InstructionHelper>
          Which of your NFTs do you want to leave a comment for?
        </InstructionHelper>
        <InputLabel css={{ marginTop: 24 }}>Select Edition</InputLabel>
        <Select
          value={index.toString()}
          onValueChange={value => {
            setIndex(parseInt(value));
          }}
        >
          {nfts &&
            nfts.map((nft, index) => {
              return (
                <Item key={nft.id} value={index.toString()} showIcon={false}>
                  <div>#{nft.serialNumber.toString()}</div>
                  <ChipContainer>{nft.comment?.id && commentedChip}</ChipContainer>
                </Item>
              );
            })}
          <div ref={bottomRef} />
        </Select>
      </Content>
    </ModalWrapper>
  );

  return (
    <Modal
      headerText="Select Edition"
      body={body}
      closeModal={closeCancelModal}
      containerStyles={containerStyles}
      headerStyles={headerStyles}
      footer={footer}
    />
  );
}

const InputLabel = styled('div', {
  marginBottom: 12,
  base: 'M',
  fontWeight: '600',
  '@tablet': {
    base: 'L',
  },
});

const ChipContainer = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  textAlign: 'right',
  gap: 8,
  marginRight: 10,
});

const Chip = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 7,
  backgroundColor: '$brand50',
  color: '$brand800',
  fontStyle: 'medium',
  borderRadius: 24,
  padding: '2px 8px',
  fontSize: 13,
  alignItems: 'center',
});
